
import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  fetchMacroCurriculum, 
  fetchMesoCurriculum,
  MacroCurriculum,
  MesoCurriculum
} from '@/services/roadmap/roadmapService';
import { ZoomableTimeline } from '@/components/roadmap/timeline';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';

export function RoadmapPreviewTab() {
  const [macroData, setMacroData] = useState<MacroCurriculum[]>([]);
  const [mesoData, setMesoData] = useState<MesoCurriculum[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [macroResult, mesoResult] = await Promise.all([
        fetchMacroCurriculum(),
        fetchMesoCurriculum()
      ]);
      
      setMacroData(macroResult.filter(item => item.is_active));
      setMesoData(mesoResult.filter(item => item.is_active));
    } catch (error) {
      console.error('Error loading curriculum data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load curriculum data for preview',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Roadmap Preview</h2>
        <Link 
          to="/roadmap" 
          className="text-sm text-primary hover:underline"
        >
          Open full roadmap
        </Link>
      </div>
      
      <p className="text-muted-foreground">
        Preview the roadmap with zoom functionality as it will appear to users. Changes made in the curriculum tabs will be reflected here after saving.
      </p>
      
      {loading ? (
        <div className="flex justify-center items-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : macroData.length === 0 || mesoData.length === 0 ? (
        <Card className="p-6 bg-background">
          <p className="text-center text-muted-foreground">
            No curriculum data available. Please create curriculum entries in the Macro and Meso tabs.
          </p>
        </Card>
      ) : (
        <Card className="p-4 bg-background">
          <ZoomableTimeline 
            macroData={macroData} 
            mesoData={mesoData} 
          />
        </Card>
      )}
    </div>
  );
}
